import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TemplateEditor } from './TemplateEditor';
import { t } from '../lib/i18n';
import { formatDateTime } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  listEmailMessages,
  listEmailTemplates,
  listSuppressions,
  type EmailMessageView,
  type EmailTemplateView,
  type SuppressionView,
} from './api';

/**
 * Transactional e-mail (context.md sections 41-44).
 *
 * Three tabs because they answer three different questions, and the middle one is the one people
 * actually open: "did it arrive?". The suppression list is beside it on purpose — an address that
 * stopped receiving mail is usually suppressed rather than broken, and that is invisible unless the
 * two are next to each other.
 */

const STATUS_TONE: Record<string, BadgeTone> = {
  SENT: 'positive',
  DELIVERED: 'positive',
  QUEUED: 'neutral',
  SENDING: 'info',
  BOUNCED: 'danger',
  COMPLAINED: 'danger',
  FAILED: 'danger',
  SUPPRESSED: 'warning',
};

type Tab = 'messages' | 'templates' | 'suppressions';

export function EmailPage(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>('messages');

  return (
    <div className="view">
      <PageHeader title={t('nav.email')} description={t('email.description')} />

      <div className="tabs" role="tablist">
        {(['messages', 'templates', 'suppressions'] as Tab[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className="tab"
            onClick={() => setTab(key)}
          >
            {t(`email.tab.${key}`)}
          </button>
        ))}
      </div>

      {tab === 'messages' && <Messages />}
      {tab === 'templates' && <Templates />}
      {tab === 'suppressions' && <Suppressions />}
    </div>
  );
}

function Messages(): React.JSX.Element {
  const [status, setStatus] = useState('');

  const messages = useQuery({
    queryKey: ['email-messages', status],
    queryFn: () => listEmailMessages(status || undefined),
  });

  const columns: Column<EmailMessageView>[] = [
    {
      key: 'recipient',
      header: t('email.recipient'),
      render: (message) => (
        <span className="cell-stack">
          <span className="strong">{message.recipient}</span>
          <span className="muted small">{message.subject}</span>
        </span>
      ),
    },
    {
      key: 'template',
      header: t('email.template'),
      secondary: true,
      render: (message) => (message.templateKey ? <code>{message.templateKey}</code> : '—'),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      render: (message) => (
        <span className="cell-stack">
          <Badge tone={STATUS_TONE[message.status] ?? 'neutral'}>
            {t(`email.state.${message.status}`)}
          </Badge>
          {message.failureReason && (
            <span className="muted small text-down">{message.failureReason}</span>
          )}
        </span>
      ),
    },
    {
      key: 'attempts',
      header: t('email.attempts'),
      secondary: true,
      align: 'end',
      render: (message) => String(message.attempts),
    },
    {
      key: 'when',
      header: t('email.sentAt'),
      align: 'end',
      render: (message) => formatDateTime(message.sentAt ?? message.queuedAt),
    },
  ];

  return (
    <>
      <div className="toolbar">
        <select
          className="select"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          aria-label={t('email.filterStatus')}
        >
          <option value="">{t('email.allStatuses')}</option>
          {['QUEUED', 'SENT', 'DELIVERED', 'BOUNCED', 'COMPLAINED', 'FAILED', 'SUPPRESSED'].map(
            (entry) => (
              <option key={entry} value={entry}>
                {t(`email.state.${entry}`)}
              </option>
            ),
          )}
        </select>
      </div>

      <Panel>
        <DataTable
          tableKey="email"
          rows={messages.data?.messages}
          columns={columns}
          rowKey={(message) => message.id}
          isLoading={messages.isLoading}
          error={messages.error}
          onRetry={() => void messages.refetch()}
          empty={{ title: t('email.noMessages'), description: t('email.noMessagesBody') }}
        />
      </Panel>
    </>
  );
}

function Templates(): React.JSX.Element {
  const templates = useQuery({ queryKey: ['email-templates'], queryFn: listEmailTemplates });

  /*
   * Which row is open, by key rather than by value (Phase 133).
   *
   * Holding the row object would leave the drawer showing the text from before a save, since the
   * list refetches into new objects; the key follows the refreshed row, so the "Padrão da
   * plataforma" badge in the drawer flips the moment the override exists.
   */
  const [editing, setEditing] = useState<string | null>(null);
  const open = templates.data?.templates.find((template) => template.key === editing) ?? null;

  const columns: Column<EmailTemplateView>[] = [
    {
      key: 'key',
      header: t('email.template'),
      render: (template) => (
        <span className="cell-stack">
          <span className="strong">{template.name}</span>
          <span className="muted small">
            <code>{template.key}</code>
          </span>
        </span>
      ),
    },
    {
      key: 'subject',
      header: t('email.subject'),
      render: (template) => template.subject,
    },
    {
      key: 'kind',
      header: t('email.kind'),
      secondary: true,
      render: (template) => <Badge tone="info">{template.kind}</Badge>,
    },
    {
      key: 'override',
      header: t('email.override'),
      align: 'end',
      render: (template) =>
        template.isOverride ? (
          <Badge tone="warning">{t('email.customised')}</Badge>
        ) : (
          <span className="muted">{t('email.platformDefault')}</span>
        ),
    },
  ];

  return (
    <Panel description={t('email.templatesHint')}>
      <DataTable
        tableKey="email-2"
        rows={templates.data?.templates}
        columns={columns}
        rowKey={(template) => template.key}
        onRowClick={(template) => setEditing(template.key)}
        isLoading={templates.isLoading}
        error={templates.error}
        onRetry={() => void templates.refetch()}
        empty={{ title: t('email.noTemplates') }}
      />

      {open && <TemplateEditor template={open} onClose={() => setEditing(null)} />}
    </Panel>
  );
}

function Suppressions(): React.JSX.Element {
  const suppressions = useQuery({ queryKey: ['suppressions'], queryFn: listSuppressions });

  const columns: Column<SuppressionView>[] = [
    {
      key: 'recipient',
      header: t('email.recipient'),
      render: (entry) => <span className="strong">{entry.recipient}</span>,
    },
    {
      key: 'reason',
      header: t('email.reason'),
      render: (entry) => (
        <span className="cell-stack">
          <Badge tone={entry.reason === 'UNSUBSCRIBE' ? 'neutral' : 'danger'}>
            {t(`email.reason.${entry.reason}`)}
          </Badge>
          {entry.detail && <span className="muted small">{entry.detail}</span>}
        </span>
      ),
    },
    {
      key: 'expires',
      header: t('email.expiresAt'),
      secondary: true,
      render: (entry) => (entry.expiresAt ? formatDateTime(entry.expiresAt) : t('email.permanent')),
    },
    {
      key: 'created',
      header: t('common.createdAt'),
      secondary: true,
      align: 'end',
      render: (entry) => formatDateTime(entry.createdAt),
    },
  ];

  return (
    <Panel>
      <DataTable
        tableKey="email-3"
        rows={suppressions.data?.suppressions}
        columns={columns}
        rowKey={(entry) => entry.recipient}
        isLoading={suppressions.isLoading}
        error={suppressions.error}
        onRetry={() => void suppressions.refetch()}
        empty={{ title: t('email.noSuppressions'), description: t('email.noSuppressionsBody') }}
      />
    </Panel>
  );
}
